// FEJLESZTÉSEK (belső felület) — EGY halmaz, KÉT NÉZET: **Board** (a folyamat) és **Idővonal** (a
// kiadás-történet). A felső váltó nem adatot vág ketté, hanem olvasatot vált.
//
// MIÉRT VÁLTOZOTT (user-döntés): korábban két fül két TÁBLÁT mutatott (Roadmap / Változásnapló), ami két
// adathalmaznak látszott, pedig egy folyamat: ötlet → tervezett → fejlesztés alatt → kiadva. Élő adaton
// mérve ráadásul a változásnapló-fül ÜRES volt (47 tételből nulla kiadott), tehát a navigáció fele egy üres
// lapra vitt. A board azt mondja meg, hol tart a munka; az idővonal azt, mi készült el és mikor. Egy kiadott
// tétel MINDKETTŐN szerepel — a boardon a friss végén, az idővonalon örökre —, és épp ez a lényeg.
//
// Az elrendezés a közös full-page lista-minta (`assets.tsx`): a váltó legfölül, alatta a KÖZÖS `ListToolbar`
// (kereső + „Szűrők (N)" popover + darabszám + „Új" gomb), majd a NÉZET.
//
// MOBILON MINDIG AZ IDŐVONAL: egy négy oszlopos, oldalra húzható board 375 px-en használhatatlan. Nem
// csonkolt boardot adunk, hanem a másik, arra a méretre jó nézetet.
//
// A KÁRTYA JELEI: a cím alatt a tétel TERMÉSZETE (típus + a PUBLIKUS láthatóság), és csak a KIVÉTEL —
// a „belső" és a „kihirdetve" a normál eset, jelvényként minden kártyán zaj lenne. A `stage` és a `status`
// FÜGGETLEN dimenzió (egy kész tétel is lehet még piszkozat): a fázis a board OSZLOPA, a piszkozat-státusz
// pedig saját jelvény a kártyán.
//
// A szűrők a popoverben élnek, nem a váltó mellett: négy legördülő ~1024–1150px-en a második sorba tördelt,
// és a sáv kilógott a tartalomra (ezt a `ListToolbar` fejléc-kommentje is kimondja).
//
// MIÉRT KÉT NÉZET EGY LAPON (és nem két menüpont): ugyanaz az entitás, csak más életszakaszban — a boardon a
// nyitott folyamat (a haladás sorrendjében) plusz a friss kiadások, az idővonalon a teljes kiadás-történet
// (dátum szerint). Két menüpont két adat-modellt sugallna, és a „mikor kerül át?" kérdésre nem adna választ.
//
// A RENDEZÉS NÉZETENKÉNT KÖTÖTT, és ez szándékos: a boardon a kurátori sorrend (horizont + pozíció) maga az
// információ, az idővonalon a kiadás dátuma. Egy szabadon rendezhető tábla itt elveszejtené a jelentést.
import type { ProductUpdateDetail, ProductUpdateRow } from '@atlas/contracts';
import {
  ArrowUturnLeftIcon,
  ClockIcon,
  MegaphoneIcon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline';
import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ProductUpdateFormSheet } from '@/components/product-updates/product-update-form-sheet';
import { ProductUpdatePublishDialog } from '@/components/product-updates/product-update-publish-dialog';
import { ProductUpdateBoard } from '@/components/product-updates/product-update-board';
import { ProductUpdateTimeline } from '@/components/product-updates/product-update-timeline';
import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { EmptyState } from '@/components/shared/empty-state';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListNewButton } from '@/components/shared/list-new-button';
import {
  DeleteRowAction,
  EditRowAction,
  RowActionButton,
  RowActions,
} from '@/components/shared/list-row-actions';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { productUpdateKeys, type ProductUpdateTab } from '@/features/product-updates/api';
import {
  PRODUCT_UPDATE_STAGES,
  PRODUCT_UPDATE_STATUSES,
  PRODUCT_UPDATE_TYPES,
} from '@/features/product-updates/enums';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

/** A lap URL-állapota: a fül + a szűrők (linkelhető, F5-biztos nézet) + a `?edit=` deep-link. */
const productUpdatesSearchSchema = z.object({
  tab: z.string().optional(),
  q: z.string().optional(),
  stage: z.string().optional(),
  type: z.string().optional(),
  status: z.string().optional(),
  visibility: z.string().optional(),
  edit: z.string().optional(),
});

export const Route = createFileRoute('/product-updates')({
  validateSearch: (search) => productUpdatesSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: ProductUpdatesRoute,
});

const PAGE_SIZE = 25;
const ALL = 'all';

function ProductUpdatesRoute(): JSX.Element {
  const { edit } = Route.useSearch();
  const navigate = useNavigate();
  const clearDeepLink = useCallback(() => {
    void navigate({
      to: '/product-updates',
      search: (prev) => ({ ...prev, edit: undefined }),
      replace: true,
    });
  }, [navigate]);
  return <ProductUpdatesPage deepLinkEditId={edit} onClearDeepLink={clearDeepLink} />;
}

export interface ProductUpdatesPageProps {
  deepLinkEditId?: string | undefined;
  onClearDeepLink?: () => void;
}

export function ProductUpdatesPage({
  deepLinkEditId,
  onClearDeepLink,
}: ProductUpdatesPageProps = {}): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  // A cím KÉTNYELVŰ a dráton (`{hu, en}`), és az `en` opcionális — a szerződés szerint a felület `hu`-ra
  // esik vissza, ha nincs fordítás. A lista korábban MINDIG a magyar címet írta ki, angol felületen is.
  const lang: 'hu' | 'en' = i18n.language === 'en' ? 'en' : 'hu';
  const titleOf = useCallback(
    (row: Pick<ProductUpdateRow, 'title'>): string =>
      lang === 'en' && row.title.en ? row.title.en : row.title.hu,
    [lang],
  );
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('product_updates.view');
  const canManage = can('product_updates.manage');
  const canPublish = can('product_updates.publish');
  const canDelete = can('product_updates.delete');

  const { filters: urlFilters, setFilters } = useListFilters({
    tab: 'board',
    q: '',
    stage: ALL,
    type: ALL,
    status: ALL,
    visibility: ALL,
  });
  // MOBILON a board nem választható: a `urlTab` az URL-é, a `tab` az, ami TÉNYLEGESEN renderel. Az URL-t
  // nem írjuk át (a link asztali gépen ugyanazt nyitja) — a mobil csak a megjelenítést köti meg.
  const isMobile = useIsMobile();
  const urlTab: ProductUpdateTab = urlFilters.tab === 'timeline' ? 'timeline' : 'board';
  const tab: ProductUpdateTab = isMobile ? 'timeline' : urlTab;
  const q = String(urlFilters.q);
  const stage = String(urlFilters.stage);
  const type = String(urlFilters.type);
  const status = String(urlFilters.status);
  const visibility = String(urlFilters.visibility);

  // A beviteli mező azonnali állapota lokális; az URL-be a debounce-olt érték kerül (a közös lista-minta).
  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(deepLinkEditId ?? null);
  const [publishTarget, setPublishTarget] = useState<ProductUpdateRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProductUpdateRow | null>(null);

  useEffect(() => {
    if (deepLinkEditId !== undefined) setEditId(deepLinkEditId);
  }, [deepLinkEditId]);

  const gated = !permsLoading && !canView;
  const listFilters = { tab, q, stage, type, status, visibility };
  const listKey = productUpdateKeys.list(listFilters);

  // VÉGTELEN GÖRGETÉS, nem lapozó: az IDŐVONALON a kiadás-történet évekig nő, és a lapon SOHA nem volt
  // lapozó-vezérlő — a régi tábla is csak az első 25 sort mutatta, némán (élő adaton mérve: 25 / 77). A
  // BOARD egy lapban jön (a szerver a teljes folyamatot adja), tehát ott a `getNextPageParam` azonnal
  // véget ér. A repo közös lista-mintája ugyanez (`contacts.tsx`, `assets.tsx`).
  const listQuery = useInfiniteQuery({
    queryKey: listKey,
    enabled: !gated,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api.productUpdates.list.query({
        query: {
          tab,
          ...(q === '' ? {} : { q }),
          // A FÁZIS-SZŰRŐ CSAK A BOARDON megy el a szerverre. Az idővonal minden sora `shipped`, tehát ott
          // a szűrő vagy no-op (`shipped`), vagy garantáltan üres lapot ad — és a vezérlő sincs kint, hogy
          // fel lehessen oldani. MOBILON ez nem elméleti: a nézet kényszerítve idővonal, miközben az URL-ben
          // ottmaradhat egy asztali gépen beállított `stage=on_hold`.
          ...(tab === 'board' && stage !== ALL ? { stage: stage as never } : {}),
          ...(type === ALL ? {} : { type: type as never }),
          ...(status === ALL ? {} : { status: status as never }),
          ...(visibility === ALL ? {} : { visibility: visibility as never }),
          page: pageParam,
          pageSize: PAGE_SIZE,
        },
      }),
    getNextPageParam: (last, all) => {
      if (last.status !== 200) return undefined;
      const loaded = all.reduce((n, p) => n + (p.status === 200 ? p.body.data.length : 0), 0);
      return loaded < last.body.total ? all.length + 1 : undefined;
    },
  });

  const pages = useMemo(() => listQuery.data?.pages ?? [], [listQuery.data]);
  const rows = useMemo(() => pages.flatMap((p) => (p.status === 200 ? p.body.data : [])), [pages]);
  const okPage = pages.find((p) => p.status === 200);
  const body = okPage?.status === 200 ? okPage.body : null;
  const counts = body?.counts ?? { board: 0, timeline: 0, draft: 0 };
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));
  // A ts-rest kliens nem dob non-2xx-re, csak státuszt ad → a lapok státuszából olvassuk a hiba-állapotot.
  const isError = gated || listQuery.isError || (pages.length > 0 && body === null);
  const { fetchNextPage } = listQuery;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // A SZERKESZTÉS a RÉSZLETET kéri le (a törzs + a csatolmányok csak ott jönnek) — a sheet ezt kapja.
  const detailKey = productUpdateKeys.detail(editId ?? 'none');
  const detailQuery = api.productUpdates.detail.useQuery(
    detailKey,
    { params: { id: editId ?? 'none' } },
    { queryKey: detailKey, enabled: editId !== null },
  );
  const editItem: ProductUpdateDetail | null =
    detailQuery.data?.status === 200 ? detailQuery.data.body : null;

  const closeEdit = useCallback(() => {
    setEditId(null);
    onClearDeepLink?.();
  }, [onClearDeepLink]);

  const removeMutation = useEntityMutation<{ id: string }, unknown>({
    mutationFn: ({ id }) => api.productUpdates.remove.mutation({ params: { id } }),
    invalidateKeys: [productUpdateKeys.lists()],
    messages: {
      success: t('toast:productUpdates.deleted'),
      conflict: t('toast:productUpdates.conflict'),
      forbidden: t('toast:productUpdates.forbidden'),
      validation: t('toast:productUpdates.validation'),
      error: t('toast:productUpdates.deleteError'),
    },
  });
  const confirmDelete = useCallback(() => {
    if (deleteTarget) {
      removeMutation.mutate({ id: deleteTarget.id }, { onSettled: () => setDeleteTarget(null) });
    }
  }, [deleteTarget, removeMutation]);

  const formatDate = useCallback(
    (iso: string | null): string => {
      if (!iso) return '';
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(locale);
    },
    [locale],
  );

  /**
   * A BOARD-kártya idő-sora: horizont + cél-időszak — SZÁNDÉKOSAN nem határidő. A boardon ez a kurátori
   * információ („mikor tájékán"), a kiadott tételnél viszont a valódi kiadás-dátum áll.
   */
  const boardMeta = useCallback(
    (row: ProductUpdateRow): ReactNode => {
      if (row.stage === 'shipped') return formatDate(row.shippedAt) || null;
      if (!row.horizon && !row.targetPeriod) return null;
      return (
        <>
          {/* A HORIZONT KISBETŰS: enum-ÉRTÉK, nem mondat-kezdet — ugyanaz a design-szabály, ami a
              jelvényeket is kisbetűsíti (`domain-badge` fejléc-komment). A cél-időszak viszont NEM: az
              adat (`2026-Q4`), nem szótári érték. */}
          {row.horizon !== null && (
            <span className="lowercase">{t(`productUpdates.horizon.${row.horizon}`)}</span>
          )}
          {row.horizon !== null && row.targetPeriod !== null ? ' · ' : null}
          {row.targetPeriod}
        </>
      );
    },
    [t, formatDate],
  );

  /** Az IDŐVONAL-kártya idő-sora: a kiadás dátuma (a csoport-fejléc a hónapot már megmondta). */
  const shippedMeta = useCallback(
    (row: ProductUpdateRow): ReactNode => formatDate(row.shippedAt) || null,
    [formatDate],
  );

  /** A sor-akciók — a boardon és az idővonalon UGYANAZOK (egy tétel, egy művelet-készlet). */
  const rowActions = useCallback(
    (row: ProductUpdateRow): ReactNode =>
      canManage || canPublish || canDelete ? (
        <RowActions>
          {/* Megafon = kihirdetés, visszafordító nyíl = visszavonás (a felirat a tooltipben). */}
          {canPublish && (
            <RowActionButton
              label={
                row.status === 'published'
                  ? t('productUpdates.revoke')
                  : t('productUpdates.publish')
              }
              onClick={() => setPublishTarget(row)}
              icon={row.status === 'published' ? ArrowUturnLeftIcon : MegaphoneIcon}
            />
          )}
          {canManage && (
            <EditRowAction label={t('productUpdates.edit')} onClick={() => setEditId(row.id)} />
          )}
          {canDelete && (
            <DeleteRowAction
              label={t('productUpdates.delete')}
              onClick={() => setDeleteTarget(row)}
            />
          )}
        </RowActions>
      ) : null,
    [canManage, canPublish, canDelete, t],
  );

  const hasFilters =
    q !== '' || stage !== ALL || type !== ALL || status !== ALL || visibility !== ALL;

  return (
    <AppShell
      toolbar={
        // A FÜL-VÁLTÓ a KERESŐ FÖLÖTT, alatta a KÖZÖS `ListToolbar` (kereső + „Szűrők (N)" popover +
        // darabszám-meta + „Új" gomb) — az eszközök-oldal (`assets.tsx`) és a jogosultságok-oldal egységes
        // mintája. A szűrők SZÁNDÉKOSAN a popoverben vannak, nem a sávban: négy legördülő a fül-sor mellett
        // ~1024–1150px-en a második sorba tördelt, és a sáv kilógott a tábla fejlécére.
        <div className="flex flex-col">
          <div className="px-4 pt-2">
            <Tabs
              value={tab}
              // Az idővonalra váltás TÖRLI a fázis-szűrőt: ott a vezérlő nincs kint, tehát egy ottmaradt
              // érték LÁTHATATLANUL szűkítene (a „szünetel" szűrő üres idővonalat adna, feloldhatatlanul).
              onValueChange={(v) =>
                setFilters(v === 'timeline' ? { tab: 'timeline', stage: ALL } : { tab: 'board' })
              }
            >
              <TabsList>
                {/* AZ IKONOK A NÉZET ALAKJÁT jelölik, nem a tartalmát: oszlopok a boardhoz, óra az
                    idővonalhoz. A régi pár (rakéta = Roadmap, newspaper = Változásnapló) a TARTALMAT nevezte
                    meg — az a két megszűnt fülhöz tartozott, és ugyanarra az adathalmazra már félrevezető.
                    A tartalom-ikonok ott MARADNAK, ahol tartalmat jelölnek: a partner-portál „Mi újság" /
                    „Min dolgozunk" fülein, és a megafon a sor „Kihirdetés" akcióján. */}
                <TabsTrigger value="board" disabled={isMobile}>
                  <ViewColumnsIcon />
                  {t('productUpdates.boardTab')}
                  <Badge variant="secondary" className="ml-1.5">
                    {counts.board}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="timeline">
                  <ClockIcon />
                  {t('productUpdates.timelineTab')}
                  <Badge variant="secondary" className="ml-1.5">
                    {counts.timeline}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <ListToolbar
            search={{
              value: searchInput,
              onChange: setSearchInput,
              placeholder: t('common.searchPlaceholder'),
            }}
            // A SZŰRŐK SORRENDJE a lista olvasási rendjét követi: előbb a tétel TERMÉSZETE (típus,
            // láthatóság — ez a két jel a cím alatt áll), utána az ÉLETSZAKASZ (fázis, majd a záró
            // oszloppal egyező státusz).
            filters={[
              {
                key: 'type',
                label: t('productUpdates.filters.type'),
                value: type,
                onChange: (v: string) => setFilters({ type: v }),
                options: PRODUCT_UPDATE_TYPES.map((tp) => ({
                  value: tp,
                  label: t(`productUpdates.type.${tp}`),
                })),
                allValue: ALL,
              },
              {
                key: 'visibility',
                label: t('productUpdates.filters.visibility'),
                value: visibility,
                onChange: (v: string) => setFilters({ visibility: v }),
                options: [
                  { value: 'internal', label: t('productUpdates.visibility.internal') },
                  { value: 'public', label: t('productUpdates.visibility.public') },
                ],
                allValue: ALL,
              },
              // A FÁZIS-SZŰRŐ CSAK A BOARDON áll ki. Az IDŐVONAL definíció szerint a kiadás-történet
              // (a BFF ott `stage === 'shipped'`-re szűr), tehát a hat fázisból ÖT sosem adhat találatot:
              // „szünetel"-t választva a lap üres marad, és semmi nem mondja meg, miért. Egy szűrő-érték,
              // ami csak üres képernyőt tud adni, nem szűrő — ezért itt nincs is.
              ...(tab === 'board'
                ? [
                    {
                      key: 'stage',
                      label: t('productUpdates.filters.stage'),
                      value: stage,
                      onChange: (v: string) => setFilters({ stage: v }),
                      options: PRODUCT_UPDATE_STAGES.map((sg) => ({
                        value: sg,
                        label: t(`productUpdates.stage.${sg}`),
                      })),
                      allValue: ALL,
                    },
                  ]
                : []),
              {
                key: 'status',
                label: t('productUpdates.filters.status'),
                value: status,
                onChange: (v: string) => setFilters({ status: v }),
                options: PRODUCT_UPDATE_STATUSES.map((st) => ({
                  value: st,
                  label: t(`productUpdates.status.${st}`),
                })),
                allValue: ALL,
              },
            ]}
            meta={
              listQuery.isFetching
                ? t('productUpdates.loading')
                : !isError
                  ? t('productUpdates.countOf', {
                      shown: rows.length,
                      total: body?.total ?? 0,
                    })
                  : ''
            }
            actions={
              <div className="hidden md:block">
                <ListNewButton
                  label={t('productUpdates.new')}
                  onClick={() => setCreateOpen(true)}
                  enabled={canManage}
                  disabledTooltip={t('common.noCreatePermission')}
                />
              </div>
            }
          />
        </div>
      }
    >
      <div className="flex h-full flex-col">
        <div className="flex min-h-0 flex-1 flex-col px-4 pb-2">
          {isError || (!gated && (permsLoading || listQuery.isLoading)) || rows.length === 0 ? (
            // A TÖLTÉS/HIBA/ÜRES állapot közös mindkét nézetre — a `DataTable` ezeket eddig maga adta, itt
            // a nézet-független keret hordozza (a board és az idővonal csak ADATOT rajzol).
            <div className="flex min-h-0 flex-1 flex-col justify-center">
              {isError ? (
                <ListErrorState forbidden={isForbidden} onRetry={() => void listQuery.refetch()} />
              ) : permsLoading || listQuery.isLoading ? (
                <div className="space-y-2">
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : hasFilters ? (
                <EmptyState
                  title={t('productUpdates.noMatches')}
                  description={t('productUpdates.noMatchesDesc')}
                />
              ) : (
                <EmptyState
                  title={
                    tab === 'timeline'
                      ? t('productUpdates.emptyTimeline')
                      : t('productUpdates.emptyBoard')
                  }
                  description={t('productUpdates.emptyDesc')}
                />
              )}
            </div>
          ) : tab === 'timeline' ? (
            <ProductUpdateTimeline
              rows={rows}
              title={titleOf}
              meta={(row) => shippedMeta(row)}
              onOpen={canManage ? (row) => setEditId(row.id) : undefined}
              actions={rowActions}
              hasNextPage={listQuery.hasNextPage}
              isFetchingNextPage={listQuery.isFetchingNextPage}
              onLoadMore={loadMore}
            />
          ) : (
            <ProductUpdateBoard
              rows={rows}
              title={titleOf}
              meta={(row) => boardMeta(row)}
              onOpen={canManage ? (row) => setEditId(row.id) : undefined}
              actions={rowActions}
              onShowOffBoard={(stageKey) => setFilters({ stage: stageKey })}
              stageFilter={stage === ALL ? null : stage}
              onClearStageFilter={() => setFilters({ stage: ALL })}
            />
          )}
        </div>
      </div>

      <ProductUpdateFormSheet open={createOpen} onOpenChange={setCreateOpen} item={null} />
      <ProductUpdateFormSheet
        open={editId !== null && editItem !== null}
        onOpenChange={(open) => {
          if (!open) closeEdit();
        }}
        item={editItem}
      />

      <ProductUpdatePublishDialog
        row={publishTarget}
        onClose={() => setPublishTarget(null)}
        listKey={listKey}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('productUpdates.deleteTitle')}
        description={t('productUpdates.deleteDesc', {
          name: deleteTarget ? titleOf(deleteTarget) : '',
        })}
        confirmLabel={t('productUpdates.delete')}
        cancelLabel={t('productUpdates.cancel')}
        pendingLabel={t('productUpdates.deleting')}
        destructive
        pending={removeMutation.isPending}
        onConfirm={confirmDelete}
      />
    </AppShell>
  );
}
